import React from 'react';
import { CheckCircle2, ChevronDown, Circle } from 'lucide-react';
import Card, { CardContent } from './Card';

interface CollapsibleSectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  /** Short line shown when collapsed */
  summary?: string;
  /** Optional leading icon (accent-tinted circle). */
  icon?: React.ReactNode;
  /** Section completion ticker (empty circle / green check). */
  complete?: boolean;
  children: React.ReactNode;
}

/** Card section with a clickable header to expand/collapse content. */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  open,
  onToggle,
  summary,
  icon,
  complete = false,
  children,
}) => {
  return (
    <Card fitContent padding="none" className="relative overflow-hidden">
      {open ? (
        <span
          className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-primary-500"
          aria-hidden
        />
      ) : null}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-3 pl-3.5 text-left transition-colors hover:bg-gray-50 sm:px-4 sm:pl-[1.125rem] xl:px-6 xl:pl-7"
      >
        {icon ? (
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100/60 text-primary-500">
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {!open && summary ? (
            <p className="mt-0.5 truncate text-sm text-gray-500">{summary}</p>
          ) : null}
        </div>
        <span className="inline-flex shrink-0 items-center gap-2" aria-hidden>
          {complete ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={2} />
          ) : (
            <Circle className="h-5 w-5 text-gray-300" strokeWidth={2} />
          )}
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>
      {open ? (
        <CardContent className="px-3 pb-3 pt-4 sm:px-4 sm:pb-4 xl:px-6 xl:pb-6">
          {children}
        </CardContent>
      ) : null}
    </Card>
  );
};

export default CollapsibleSection;
